import React, { useState } from 'react';
import { Box, Grid, Typography, Paper, Button, Snackbar, Alert } from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { useNavigate } from 'react-router-dom';

import { PageHeader, FilterChips, RowThumb, SkeletonCards, EmptyState, ErrorState, PermissionState, useDesignTokens } from '../ui';
import { formatCurrency } from '../../utils/currencyFormatter';
import { radius } from '../../themes/smartinventoryTheme';
import useBrowseProductsPage from './useBrowseProductsPage';
import AddToCartControl from './AddToCartControl';
import { useCart } from '../../contexts/CartContext';

const BrowseProducts = () => {
  const p = useBrowseProductsPage();
  const navigate = useNavigate();
  const { cart, addToCart } = useCart();
  const { categoryColors } = useDesignTokens();
  const [addedToast, setAddedToast] = useState({ open: false, message: '' });
  const closeAddedToast = () => setAddedToast((s) => ({ ...s, open: false }));

  const handleAddToCart = async (item, qty) => {
    const result = await addToCart(item.productId, qty);
    if (result?.success) {
      setAddedToast({
        open: true,
        message: p.t('{{name}} added to cart', { name: item.productName, defaultValue: `${item.productName} added to cart` }),
      });
    }
  };

  const categoryById = {};
  p.categories.forEach((c) => { categoryById[c._id] = c; });

  const filterOptions = [
    { value: 'all', label: p.t('All Categories') },
    ...p.categories.map((c) => ({ value: c._id, label: c.categoryName, dotColor: c.categoryColor })),
  ];

  const quantityInCart = (productId) => cart?.items?.find((item) => item.productId === productId)?.quantity || 0;

  return (
    <Box>
      <PageHeader
        title={p.t('Browse products', { defaultValue: 'Browse products' })}
        subtitle={p.t('{{count}} products', { count: p.total, defaultValue: `${p.total} products` })}
        search={{ value: p.searchInput, onChange: (e) => p.setSearchInput(e.target.value), placeholder: p.t('Search products', { defaultValue: 'Search products' }) }}
        actions={[
          { label: p.t('Cart · {{count}}', { count: cart?.itemCount || 0, defaultValue: `Cart · ${cart?.itemCount || 0}` }), variant: 'contained', onClick: () => navigate('/cart') },
        ]}
      />

      <Box sx={{ mb: 2 }}>
        <FilterChips options={filterOptions} value={p.categoryFilter} onChange={p.setCategoryFilter} filtersLabel={p.t('Filters')} />
      </Box>

      {p.effectiveStatus === 'loading' && <SkeletonCards rows={4} />}

      {p.effectiveStatus === 'error' && (
        <ErrorState
          title={p.t("Couldn't load products", { defaultValue: "Couldn't load products" })}
          description={p.t('The server did not respond. Your data is safe — nothing was changed.', { defaultValue: 'The server did not respond. Your data is safe — nothing was changed.' })}
          request={p.errorInfo?.request}
          retryLabel={p.t('Try again', { defaultValue: 'Try again' })}
          onRetry={p.fetchProducts}
        />
      )}

      {p.effectiveStatus === 'permission' && (
        <PermissionState
          title={p.t("You don't have access to the catalogue", { defaultValue: "You don't have access to the catalogue" })}
          description={p.t('Ask your workspace owner for access.', { defaultValue: 'Ask your workspace owner for access.' })}
        />
      )}

      {p.effectiveStatus === 'ready' && p.effectiveRows.length === 0 && (
        p.isNoMatch ? (
          <EmptyState
            kind="no-match"
            title={p.t('No products match "{{query}}"', { query: p.search, defaultValue: `No products match "${p.search}"` })}
            description={p.t('Try a different search or clear the category filter.', { defaultValue: 'Try a different search or clear the category filter.' })}
            actions={[{ label: p.t('Clear filters', { defaultValue: 'Clear filters' }), variant: 'text', onClick: () => { p.setSearchInput(''); p.setSearch(''); p.setCategoryFilter('all'); } }]}
          />
        ) : (
          <EmptyState
            kind="none"
            icon={Inventory2Icon}
            title={p.t('No products available yet', { defaultValue: 'No products available yet' })}
            description={p.t('Check back soon — new products will show up here.', { defaultValue: 'Check back soon — new products will show up here.' })}
          />
        )
      )}

      {p.effectiveStatus === 'ready' && p.effectiveRows.length > 0 && (
        <>
          <Grid container spacing={1.75}>
            {p.effectiveRows.map((item) => {
              const category = categoryById[item.categoryId];
              const isOutOfStock = !item.quantity || item.quantity <= 0;
              return (
                <Grid item xs={6} sm={4} md={3} key={item.invertoryId}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 1.75, cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
                    onClick={() => navigate(`/shop/${item.productId}`)}
                  >
                    <RowThumb src={item.productImage} name={item.productName} sx={{ width: '100%', height: 110, borderRadius: `${radius.md}px`, mb: 1.25, fontSize: (theme) => theme.typography.h4.fontSize }} />
                    <Typography variant="body2" fontWeight={600} noWrap>{item.productName}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, mt: 0.25 }}>
                      {category && (
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: category.categoryColor || (categoryColors.none || {}).dot, flexShrink: 0 }} />
                      )}
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {category ? `${category.categoryName} · ` : ''}{p.t('per')} {item.UOM}{isOutOfStock ? ` · ${p.t('Out of stock')}` : ''}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={700} sx={{ mb: 1 }}>{formatCurrency(item.productPrice)}</Typography>
                    <Box onClick={(e) => e.stopPropagation()}>
                      <AddToCartControl
                        available={item.quantity}
                        alreadyInCart={quantityInCart(item.productId)}
                        onAdd={(qty) => handleAddToCart(item, qty)}
                        unit={item.UOM}
                        size="small"
                        fullWidth
                      />
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          {p.remaining > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5 }}>
              <Button variant="outlined" onClick={p.loadMore} disabled={p.loadingMore} sx={{ borderRadius: `${radius.pill}px` }}>
                {p.t('Load {{count}} more · {{remaining}} remaining', {
                  count: p.nextBatchSize, remaining: p.remaining, defaultValue: `Load ${p.nextBatchSize} more · ${p.remaining} remaining`,
                })}
              </Button>
            </Box>
          )}
        </>
      )}

      <Snackbar
        open={addedToast.open}
        autoHideDuration={3000}
        onClose={closeAddedToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={closeAddedToast} sx={{ width: '100%' }}>
          {addedToast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BrowseProducts;
